/**
 * ProcessProcedureFormModal — Ustawienia → „Procedury SQL" → zakładka „Procesy" (ADR-0074), modal create/edit
 * (mirror `GlobalDictionaryFormModal` — edytor SQL + edytor parametrów + sekcja „Testuj").
 *
 * `kind` ('validation' | 'action') jest NIEEDYTOWALNY po utworzeniu — backend w ogóle nie
 * przyjmuje tego pola w `UpdateProcessProcedureSchema` (cicho ignorowane, bez osobnego kodu
 * błędu), więc UI zamraża RadioGroup w `mode==='edit'` zamiast pozwalać na próbę zmiany.
 *
 * Sekcja „Testuj" woła `POST /api/processes/procedures/test-sql` (dry-run — dla `kind='action'`
 * silnik ZAWSZE robi ROLLBACK, żadna zmiana nie jest utrwalana). Endpoint wymaga kontekstu
 * rezerwowego silnika (`procContext`: recordId/processId/instanceId/nodeId/userId jako UUID +
 * targetModule) — poza formularzem create/edit nie ma żadnej realnej instancji procesu, więc
 * pola te są domyślnie wypełnione PLACEHOLDEREM (nil UUID `00000000-…-000000000000`, userId =
 * bieżący zalogowany user) i chowane pod zwijaną sekcję „Kontekst zaawansowany" — test służy
 * kontroli składni/kształtu wyniku, nie symulacji zachowania na żywym rekordzie, więc user NIE
 * musi wpisywać 7 UUID żeby w ogóle uruchomić test.
 */

import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { isAxiosError } from 'axios';
import { ChevronDown, ChevronRight, Plus, Trash2 } from 'lucide-react';

import { Button } from '@/components/Button';
import { Checkbox } from '@/components/Checkbox';
import { Dropdown } from '@/components/Dropdown';
import { FormField } from '@/components/FormField';
import { IconButton } from '@/components/IconButton';
import { Input } from '@/components/Input';
import { InsertExampleButton } from '@/components/InsertExampleButton';
import { Modal } from '@/components/Modal';
import { RadioGroup } from '@/components/RadioGroup';
import { Textarea } from '@/components/Textarea';
import { SqlField } from '@/components/ui/SqlField';
import { useTenantAuth } from '@/contexts/TenantAuthContext';
import {
  PROCEDURE_PARAM_TYPES,
  type ProcedureParam,
  type ProcedureParamType,
  type ProcessProcedureKind,
  useTestProcessProcedureSql,
} from '@/lib/processes/procedures-api';
import {
  buildSqlParamCompletions,
  PROCESS_PROCEDURE_RESERVED_PARAMS,
} from '@/lib/sql-editor/params';
import { useTenantSqlSchema } from '@/lib/sql-editor/schema';
import { useSqlTestDiagnostics } from '@/lib/sql-editor/test-error';

export interface ProcessProcedureFormValues {
  name: string;
  description: string;
  kind: ProcessProcedureKind;
  sql: string;
  declaredParams: ProcedureParam[];
}

export function emptyProcessProcedureFormValues(): ProcessProcedureFormValues {
  return { name: '', description: '', kind: 'validation', sql: '', declaredParams: [] };
}

interface Props {
  open: boolean;
  mode: 'create' | 'edit';
  initialValues: ProcessProcedureFormValues;
  saving?: boolean;
  /** Błędy pól zmapowane z odpowiedzi 409 backendu (np. NAME_TAKEN → pole `name`). */
  serverErrors?: Record<string, string>;
  /**
   * Blokuje pole "Rodzaj procedury" nawet w trybie `create` — używane przez szybkie
   * tworzenie z panelu właściwości krawędzi (`EdgePropertiesPanel`), gdzie typ musi
   * zgadzać się ze slotem (walidacja/akcja), inaczej nowo utworzona procedura nie
   * pojawiłaby się na liście tego slotu do auto-podpięcia (ADR-0074).
   */
  lockKind?: boolean;
  onSubmit: (values: ProcessProcedureFormValues) => void;
  onClose: () => void;
}

/** Placeholder — dry-run nie odwołuje się do realnej instancji, więc nil UUID jest bezpieczny. */
const NIL_UUID = '00000000-0000-0000-0000-000000000000';
/** Placeholder modułu docelowego — czytelnie fałszywy, nie myli się z realnym kodem modułu. */
const PLACEHOLDER_TARGET_MODULE = 'test_module';

/** Mirror walidatora backendu (`paramNameSchema`, `lib/validators/process-procedures.ts`). */
const PARAM_NAME_REGEX = /^[A-Za-z_][A-Za-z0-9_]*$/;

/**
 * Migawka pól procedury do jawnego `dirty` (pytanie przy zamknięciu tłem/Esc): rodzaj procedury
 * i typ parametru wybiera się klikiem/listą (bez zdarzeń wpisywania). Pola sekcji „Testuj"
 * (wartości próbne, kontekst) NIE są częścią procedury i się nie liczą.
 */
function formSnapshot(values: ProcessProcedureFormValues): string {
  return JSON.stringify([
    values.name,
    values.description,
    values.kind,
    values.sql,
    values.declaredParams.map((p) => [p.name, p.type, p.required]),
  ]);
}

/** Mapuje kod błędu z `POST .../test-sql` na klucz i18n czytelnego komunikatu. */
function testSqlErrorKey(code: string | undefined): string {
  switch (code) {
    case 'SQL_NOT_SELECT':
      return 'tenant.processProcedures.form.sqlErrors.notSelect';
    case 'SQL_MULTIPLE_STATEMENTS':
      return 'tenant.processProcedures.form.sqlErrors.multipleStatements';
    case 'SQL_PARAM_UNDECLARED':
      return 'tenant.processProcedures.form.sqlErrors.paramUndeclared';
    case 'SQL_EMPTY':
      return 'tenant.processProcedures.form.sqlErrors.empty';
    case 'SQL_FORBIDDEN_KEYWORD':
      return 'tenant.processProcedures.form.sqlErrors.forbiddenKeyword';
    case 'SQL_UNSUPPORTED_SYNTAX':
      return 'tenant.processProcedures.form.sqlErrors.unsupportedSyntax';
    case 'EXTERNAL_PARAM_MISSING':
      return 'tenant.processProcedures.form.sqlErrors.paramMissing';
    case 'EXTERNAL_PARAM_INVALID':
      return 'tenant.processProcedures.form.sqlErrors.paramInvalid';
    case 'PROCEDURE_RESULT_SHAPE':
      return 'tenant.processProcedures.form.sqlErrors.resultShape';
    case 'EXECUTION_FAILED':
      return 'tenant.processProcedures.form.sqlErrors.executionFailed';
    default:
      return 'tenant.processProcedures.form.sqlErrors.generic';
  }
}

export function ProcessProcedureFormModal({
  open,
  mode,
  initialValues,
  saving,
  serverErrors,
  lockKind,
  onSubmit,
  onClose,
}: Props) {
  const { t } = useTranslation();
  const { me } = useTenantAuth();
  const [name, setName] = useState(initialValues.name);
  const [description, setDescription] = useState(initialValues.description);
  const [kind, setKind] = useState<ProcessProcedureKind>(initialValues.kind);
  const [sql, setSql] = useState(initialValues.sql);
  const [declaredParams, setDeclaredParams] = useState<ProcedureParam[]>(
    initialValues.declaredParams,
  );
  const [errors, setErrors] = useState<Record<string, string>>({});
  // Migawka wartości z chwili otwarcia (rodzic może przeliczyć `initialValues` przy otwartym oknie).
  const [baseline, setBaseline] = useState<string>(() => formSnapshot(initialValues));

  // Sekcja „Testuj" — przykładowe wartości zadeklarowanych parametrów + kontekst rezerwowy
  // silnika (domyślnie placeholder, opcjonalnie nadpisywalny w sekcji zaawansowanej).
  const [testParamValues, setTestParamValues] = useState<Record<string, string>>({});
  const [showAdvancedContext, setShowAdvancedContext] = useState(false);
  const [contextRecordId, setContextRecordId] = useState(NIL_UUID);
  const [contextProcessId, setContextProcessId] = useState(NIL_UUID);
  const [contextInstanceId, setContextInstanceId] = useState(NIL_UUID);
  const [contextNodeId, setContextNodeId] = useState(NIL_UUID);
  const [contextRouteKey, setContextRouteKey] = useState('');
  const [contextUserId, setContextUserId] = useState(me?.user.id ?? NIL_UUID);
  const [contextTargetModule, setContextTargetModule] = useState(PLACEHOLDER_TARGET_MODULE);

  const testMutation = useTestProcessProcedureSql();
  const sqlSchema = useTenantSqlSchema(open);
  const sqlDiagnostics = useSqlTestDiagnostics(sql, testMutation);
  const sqlParams = useMemo(
    () => buildSqlParamCompletions(t, PROCESS_PROCEDURE_RESERVED_PARAMS, declaredParams),
    [t, declaredParams],
  );

  useEffect(() => {
    if (open) {
      setName(initialValues.name);
      setDescription(initialValues.description);
      setKind(initialValues.kind);
      setSql(initialValues.sql);
      setDeclaredParams(initialValues.declaredParams);
      setBaseline(formSnapshot(initialValues));
      setTestParamValues({});
      setShowAdvancedContext(false);
      setContextRecordId(NIL_UUID);
      setContextProcessId(NIL_UUID);
      setContextInstanceId(NIL_UUID);
      setContextNodeId(NIL_UUID);
      setContextRouteKey('');
      setContextUserId(me?.user.id ?? NIL_UUID);
      setContextTargetModule(PLACEHOLDER_TARGET_MODULE);
      testMutation.reset();
      setErrors({});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  useEffect(() => {
    if (serverErrors) {
      setErrors((prev) => ({ ...prev, ...serverErrors }));
    }
  }, [serverErrors]);

  if (!open) return null;

  const isDirty = formSnapshot({ name, description, kind, sql, declaredParams }) !== baseline;

  // `kind` immutable po utworzeniu (patrz docstring pliku) LUB zablokowany przez wołającego
  // (szybkie tworzenie z panelu krawędzi — `lockKind`, patrz docstring propa).
  const isKindLocked = mode === 'edit' || lockKind === true;

  // Przykład dopasowany do rodzaju procedury — walidacja zwraca severity (SELECT),
  // akcja modyfikuje dane (INSERT/UPDATE/DELETE); mirror rozgałęzienia podpowiedzi niżej.
  const sqlExample =
    kind === 'validation'
      ? t('tenant.processProcedures.form.sqlExample')
      : t('tenant.processProcedures.form.sqlExampleAction');

  function validate(): boolean {
    const next: Record<string, string> = {};
    if (!name.trim()) {
      next.name = t('tenant.processProcedures.form.errors.nameRequired');
    } else if (name.trim().length > 200) {
      next.name = t('tenant.processProcedures.form.errors.nameLength');
    }
    if (!sql.trim()) {
      next.sql = t('tenant.processProcedures.form.errors.sqlRequired');
    }
    const names = declaredParams.map((p) => p.name.trim());
    if (names.some((n) => !PARAM_NAME_REGEX.test(n))) {
      next.declaredParams = t('tenant.processProcedures.form.errors.sqlParamNameInvalid');
    } else if (new Set(names.map((n) => n.toLowerCase())).size !== names.length) {
      next.declaredParams = t('tenant.processProcedures.form.errors.sqlParamNameDuplicate');
    }
    setErrors(next);
    return Object.keys(next).length === 0;
  }

  function handleSubmit() {
    if (!validate()) return;
    onSubmit({
      name: name.trim(),
      description: description.trim(),
      kind,
      sql: sql.trim(),
      declaredParams: declaredParams.map((p) => ({ ...p, name: p.name.trim() })),
    });
  }

  function updateParam(index: number, patch: Partial<ProcedureParam>) {
    setDeclaredParams((prev) => prev.map((p, i) => (i === index ? { ...p, ...patch } : p)));
  }

  function removeParam(index: number) {
    setDeclaredParams((prev) => prev.filter((_, i) => i !== index));
  }

  function handleTest() {
    if (!sql.trim()) return;
    const paramValues: Record<string, string> = {};
    for (const param of declaredParams) {
      const raw = testParamValues[param.name];
      if (raw !== undefined && raw !== '') paramValues[param.name] = raw;
    }
    testMutation.mutate({
      sql: sql.trim(),
      kind,
      declaredParams: declaredParams.map((p) => ({ ...p, name: p.name.trim() })),
      ...(Object.keys(paramValues).length > 0 ? { paramValues } : {}),
      procContext: {
        recordId: contextRecordId.trim(),
        processId: contextProcessId.trim(),
        instanceId: contextInstanceId.trim(),
        nodeId: contextNodeId.trim(),
        routeKey: contextRouteKey.trim() ? contextRouteKey.trim() : null,
        userId: contextUserId.trim(),
        targetModule: contextTargetModule.trim(),
      },
    });
  }

  const testError = testMutation.isError
    ? (() => {
        const err = testMutation.error;
        const data =
          isAxiosError(err) && err.response?.data && typeof err.response.data === 'object'
            ? (err.response.data as {
                error?: string;
                message?: string;
                paramName?: string;
              })
            : null;
        const code = data?.error;
        const translated = t(testSqlErrorKey(code), { paramName: data?.paramName ?? '' });
        // EXECUTION_FAILED / PROCEDURE_RESULT_SHAPE niosą realny komunikat serwera (błąd PG /
        // szczegół złego kształtu wyniku) — dokładamy go pod tłumaczonym nagłówkiem: to
        // diagnostyka dla autora SQL (endpoint gate'owany permission autoringu), nie przeciek.
        if ((code === 'EXECUTION_FAILED' || code === 'PROCEDURE_RESULT_SHAPE') && data?.message) {
          return `${translated} ${data.message}`;
        }
        return translated;
      })()
    : null;
  const testResult = testMutation.data ?? null;

  const titleKey =
    mode === 'create'
      ? 'tenant.processProcedures.form.titleCreate'
      : 'tenant.processProcedures.form.titleEdit';

  // Dwa różne powody blokady — "nie można zmienić po utworzeniu" (edit) vs "typ narzucony
  // przez slot, z którego tworzysz procedurę" (create + lockKind, patrz docstring propa).
  const kindLockedReason =
    mode === 'edit'
      ? t('tenant.processProcedures.form.kindImmutableHint')
      : t('tenant.processProcedures.form.kindLockedBySlotHint');

  const severityBannerClass: Record<'ok' | 'warning' | 'error', string> = {
    ok: 'border-green-300 bg-green-50 text-green-700 dark:border-green-700 dark:bg-green-950/40 dark:text-green-300',
    warning:
      'border-amber-300 bg-amber-50 text-amber-700 dark:border-amber-700 dark:bg-amber-950/40 dark:text-amber-300',
    error:
      'border-red-300 bg-red-50 text-red-700 dark:border-red-700 dark:bg-red-950/40 dark:text-red-300',
  };

  return (
    <Modal
      open
      onClose={onClose}
      title={t(titleKey)}
      // Szerokie okno — dane procedury w dwóch kolumnach, pod nimi pole SQL na całą szerokość,
      // na dole parametry obok „Testuj” (bez przewijania).
      size="2xl"
      testId="process-procedure-form-modal"
      guardUnsaved
      dirty={isDirty}
      closeOnBackdrop={!saving}
      closeOnEsc={!saving}
      footer={
        <>
          <Button
            variant="ghost"
            onClick={onClose}
            disabled={saving}
            data-testid="process-procedure-form-cancel"
          >
            {t('common.cancel')}
          </Button>
          <Button
            variant="primary"
            onClick={handleSubmit}
            disabled={saving || sql.trim().length === 0}
            loading={saving}
            data-testid="process-procedure-form-submit"
          >
            {mode === 'create' ? t('common.create') : t('common.save')}
          </Button>
        </>
      }
    >
      <div
        className="grid grid-cols-1 items-start gap-x-6 gap-y-3 lg:grid-cols-2"
        data-testid="process-procedure-form-layout"
      >
        <div className="space-y-3">
          <FormField label={t('tenant.processProcedures.form.name')} error={errors.name}>
            <Input
              value={name}
              onChange={(e) => {
                setName(e.target.value);
                setErrors((prev) => ({ ...prev, name: '' }));
              }}
              placeholder={t('tenant.processProcedures.form.namePlaceholder')}
              data-testid="process-procedure-form-name"
            />
          </FormField>
          <FormField label={t('tenant.processProcedures.form.description')}>
            <Textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder={t('tenant.processProcedures.form.descriptionPlaceholder')}
              rows={2}
              data-testid="process-procedure-form-description"
            />
          </FormField>
        </div>

        <div className="space-y-3">
          <RadioGroup<ProcessProcedureKind>
            name="process-procedure-kind"
            legend={t('tenant.processProcedures.form.kind')}
            value={kind}
            onChange={setKind}
            options={[
              {
                value: 'validation',
                label: t('tenant.processProcedures.form.kindValidation'),
                disabled: isKindLocked,
                disabledReason: isKindLocked ? kindLockedReason : undefined,
              },
              {
                value: 'action',
                label: t('tenant.processProcedures.form.kindAction'),
                disabled: isKindLocked,
                disabledReason: isKindLocked ? kindLockedReason : undefined,
              },
            ]}
            data-testid="process-procedure-form-kind"
          />
        </div>

        {/* Pole SQL na całą szerokość okna (obie kolumny). */}
        <div className="space-y-1 lg:col-span-2">
          <div className="flex justify-end">
            <InsertExampleButton
              currentValue={sql}
              example={sqlExample}
              onInsert={(value) => {
                setSql(value);
                setErrors((prev) => ({ ...prev, sql: '' }));
              }}
              data-testid="process-procedure-form-sql-example"
            />
          </div>
          <FormField
            label={t('tenant.processProcedures.form.sqlLabel')}
            hint={
              kind === 'validation'
                ? t('tenant.processProcedures.form.sqlHintValidation')
                : t('tenant.processProcedures.form.sqlHintAction')
            }
            error={errors.sql}
          >
            <SqlField
              value={sql}
              onChange={(value) => {
                setSql(value);
                setErrors((prev) => ({ ...prev, sql: '' }));
              }}
              schema={sqlSchema}
              parameters={sqlParams}
              diagnostics={sqlDiagnostics}
              placeholder={t('common.examplePlaceholder', {
                example: sqlExample,
              })}
              aria-label={t('tenant.processProcedures.form.sqlLabel')}
              minHeight="200px"
              data-testid="process-procedure-form-sql"
            />
          </FormField>
        </div>

        <FormField
          label={t('tenant.processProcedures.form.sqlParamsTitle')}
          hint={t('tenant.processProcedures.form.sqlParamsHint')}
          error={errors.declaredParams}
        >
          <div className="space-y-2" data-testid="process-procedure-form-params">
            {declaredParams.map((param, index) => (
              <div key={index} className="flex items-center gap-2">
                <Input
                  value={param.name}
                  onChange={(e) => {
                    updateParam(index, { name: e.target.value });
                    setErrors((prev) => ({ ...prev, declaredParams: '' }));
                  }}
                  placeholder={t('tenant.processProcedures.form.sqlParamNamePlaceholder')}
                  containerClassName="min-w-0 flex-1"
                  data-testid={`process-procedure-form-param-name-${index}`}
                />
                <Dropdown
                  className="w-32 shrink-0"
                  value={param.type}
                  onChange={(value) => updateParam(index, { type: value as ProcedureParamType })}
                  options={PROCEDURE_PARAM_TYPES.map((type) => ({
                    value: type,
                    label: t(`admin.globalDictionaries.form.paramType.${type}`),
                  }))}
                  data-testid={`process-procedure-form-param-type-${index}`}
                />
                <Checkbox
                  checked={param.required}
                  onChange={(e) => updateParam(index, { required: e.target.checked })}
                  label={t('tenant.processProcedures.form.paramRequired')}
                  containerClassName="shrink-0"
                  data-testid={`process-procedure-form-param-required-${index}`}
                />
                <IconButton
                  variant="danger"
                  size="sm"
                  onClick={() => removeParam(index)}
                  aria-label={t('tenant.processProcedures.form.sqlParamRemove')}
                  data-testid={`process-procedure-form-param-remove-${index}`}
                >
                  <Trash2 className="h-3.5 w-3.5" />
                </IconButton>
              </div>
            ))}
            <Button
              variant="secondary"
              size="sm"
              leftIcon={<Plus className="h-3.5 w-3.5" />}
              onClick={() =>
                setDeclaredParams((prev) => [...prev, { name: '', type: 'text', required: false }])
              }
              data-testid="process-procedure-form-param-add"
            >
              {t('tenant.processProcedures.form.sqlParamAdd')}
            </Button>
          </div>
        </FormField>

        {/* Sekcja „Testuj" — dry-run na bazie tenanta (patrz docstring pliku). */}
        <div
          className="space-y-2 rounded-lg border border-border-primary bg-bg-secondary p-3"
          data-testid="process-procedure-form-test-section"
        >
          <p className="text-sm font-medium text-text-primary">
            {t('tenant.processProcedures.form.testTitle')}
          </p>
          <p className="text-xs text-text-tertiary">
            {t('tenant.processProcedures.form.testHint')}
          </p>

          {declaredParams.length > 0 && (
            <div className="space-y-2">
              <p className="text-xs text-text-muted">
                {t('tenant.processProcedures.form.testParamValuesHint')}
              </p>
              {declaredParams
                .filter((param) => param.name.trim())
                .map((param) => (
                  <FormField key={param.name} label={param.name}>
                    <Input
                      value={testParamValues[param.name] ?? ''}
                      onChange={(e) =>
                        setTestParamValues((prev) => ({ ...prev, [param.name]: e.target.value }))
                      }
                      data-testid={`process-procedure-form-test-param-${param.name}`}
                    />
                  </FormField>
                ))}
            </div>
          )}

          <Button
            variant="ghost"
            size="sm"
            onClick={() => setShowAdvancedContext((v) => !v)}
            leftIcon={
              showAdvancedContext ? (
                <ChevronDown className="h-3.5 w-3.5" />
              ) : (
                <ChevronRight className="h-3.5 w-3.5" />
              )
            }
            data-testid="process-procedure-form-advanced-toggle"
          >
            {t('tenant.processProcedures.form.advancedContext')}
          </Button>

          {showAdvancedContext && (
            <div
              className="grid grid-cols-1 gap-2 sm:grid-cols-2"
              data-testid="process-procedure-form-advanced-context"
            >
              <p className="col-span-full text-xs text-text-tertiary">
                {t('tenant.processProcedures.form.advancedContextHint')}
              </p>
              <FormField label={t('tenant.processProcedures.form.contextRecordId')}>
                <Input
                  value={contextRecordId}
                  onChange={(e) => setContextRecordId(e.target.value)}
                  data-testid="process-procedure-form-context-record-id"
                />
              </FormField>
              <FormField label={t('tenant.processProcedures.form.contextProcessId')}>
                <Input
                  value={contextProcessId}
                  onChange={(e) => setContextProcessId(e.target.value)}
                  data-testid="process-procedure-form-context-process-id"
                />
              </FormField>
              <FormField label={t('tenant.processProcedures.form.contextInstanceId')}>
                <Input
                  value={contextInstanceId}
                  onChange={(e) => setContextInstanceId(e.target.value)}
                  data-testid="process-procedure-form-context-instance-id"
                />
              </FormField>
              <FormField label={t('tenant.processProcedures.form.contextNodeId')}>
                <Input
                  value={contextNodeId}
                  onChange={(e) => setContextNodeId(e.target.value)}
                  data-testid="process-procedure-form-context-node-id"
                />
              </FormField>
              <FormField label={t('tenant.processProcedures.form.contextRouteKey')}>
                <Input
                  value={contextRouteKey}
                  onChange={(e) => setContextRouteKey(e.target.value)}
                  data-testid="process-procedure-form-context-route-key"
                />
              </FormField>
              <FormField label={t('tenant.processProcedures.form.contextUserId')}>
                <Input
                  value={contextUserId}
                  onChange={(e) => setContextUserId(e.target.value)}
                  data-testid="process-procedure-form-context-user-id"
                />
              </FormField>
              <FormField label={t('tenant.processProcedures.form.contextTargetModule')}>
                <Input
                  value={contextTargetModule}
                  onChange={(e) => setContextTargetModule(e.target.value)}
                  data-testid="process-procedure-form-context-target-module"
                />
              </FormField>
            </div>
          )}

          <Button
            variant="secondary"
            size="sm"
            onClick={handleTest}
            loading={testMutation.isPending}
            disabled={testMutation.isPending || sql.trim().length === 0}
            data-testid="process-procedure-form-test-btn"
          >
            {t('tenant.processProcedures.form.testButton')}
          </Button>

          {testError && (
            <p
              role="alert"
              className="rounded-md border border-red-300 bg-red-50 px-3 py-2 text-sm text-red-700 dark:border-red-700 dark:bg-red-950/40 dark:text-red-300"
              data-testid="process-procedure-form-test-error"
            >
              {testError}
            </p>
          )}

          {testResult && testResult.kind === 'validation' && (
            <div
              className={`rounded-md border px-3 py-2 text-sm ${severityBannerClass[testResult.severity]}`}
              data-testid="process-procedure-form-test-result"
            >
              <p className="font-medium">
                {t(`tenant.processProcedures.form.testSeverity.${testResult.severity}`)}
              </p>
              {testResult.message && <p className="mt-0.5">{testResult.message}</p>}
            </div>
          )}

          {testResult && testResult.kind === 'action' && (
            <div
              className="space-y-1 rounded-md border border-border-primary bg-bg-primary px-3 py-2 text-sm text-text-secondary"
              data-testid="process-procedure-form-test-result"
            >
              <p>
                {t('tenant.processProcedures.form.testRowCount', { count: testResult.rowCount })}
              </p>
              <p className="text-xs text-text-tertiary">
                {t('tenant.processProcedures.form.testDryRunNote')}
              </p>
            </div>
          )}
        </div>
      </div>
    </Modal>
  );
}
